import React from 'react';
import { Link } from 'react-router';
import cx from 'classnames';
import _ from 'lodash';
import breakpoints from '../../util/breakpoints';
import { touchevents } from '../../util/featureDetection';
import MegaDropdown from './MegaDropdown.jsx';

const linkIsActive = (path, href) => _.startsWith(path, href);
const linkIsExternal = url => /^http/i.test(url);

export default class Nav extends React.PureComponent {
  constructor() {
    super();

    this.state = {
      showLinksInDropdownAfterThisIndex: 100,
      isPopupNavVisible: false,
    };

    this.hidePopupNav = this.hidePopupNav.bind(this);
    this.fontWidthHackCheck = this.fontWidthHackCheck.bind(this);
  }

  componentDidMount() {
    this.calculateVisibleNavItems();

    const navbarLink = document.querySelector('.navbar-link');

    if (navbarLink) {
      this.originalNavLinkWidth = navbarLink.clientWidth;
      this.fontWidthHackCheckInterval = setInterval(this.fontWidthHackCheck, 300);
      setTimeout(() => clearInterval(this.fontWidthHackCheckInterval), 5000);
    }
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.windowWidth !== this.props.windowWidth ||
      nextProps.breakpoint !== this.props.breakpoint ||
      nextProps.path !== this.props.path) {
      setTimeout(() => this.calculateVisibleNavItems());
    }
  }

  componentWillUnmount() {
    clearInterval(this.fontWidthHackCheckInterval);
  }

  getNumberOfCollapsableLinks() {
    const hasSecondaryNav = !_.isEmpty(this.props.menuLinks.secondaryMenuLinks) &&
      this.props.breakpoint >= breakpoints.LG;

    return hasSecondaryNav ?
      this.props.menuLinks.mainMenuLinks.length :
      this.getAllLinks().length;
  }

  getLinkComponent(link, className) {
    if (linkIsExternal(link.url)) {
      return <a className={className} href={link.url} target="_blank">{link.name}</a>;
    }

    return <Link className={className} to={link.url}>{link.name}</Link>;
  }

  fontWidthHackCheck() {
    const navbarLink = document.querySelector('.navbar-link');

    // Run calculateVisibleNavItems if the width of the first navbar link has changed,
    // inidicating that a new font has loaded and affected the font size
    // (if we don't do this, links might end up on a second row)
    if (navbarLink && this.originalNavLinkWidth !== navbarLink.clientWidth) {
      this.calculateVisibleNavItems();
    }
  }

  getAllLinks() {
    const links = this.props.menuLinks;
    if (links.secondaryMenuLinks) {
      return links.mainMenuLinks.concat(links.secondaryMenuLinks);
    }

    return links.mainMenuLinks;
  }

  isSecondaryLink(link) {
    return _.includes(this.props.menuLinks.secondaryMenuLinks, link);
  }

  calculateVisibleNavItems() {
    if (this.props.showOnMobile || (!this.props.showOnMobile && this.props.breakpoint >= breakpoints.MD)) {
      const numberOfCollapsableLinks = this.getNumberOfCollapsableLinks();
      const menuDOMNodes = this.refs.nav.getElementsByTagName('li');
      this.setState({ showLinksInDropdownAfterThisIndex: numberOfCollapsableLinks }, () => {
        for (let i = numberOfCollapsableLinks - 1; i >= 0; i--) {
          if (menuDOMNodes[i] && menuDOMNodes[i].offsetTop === 0) {
            this.setState({ showLinksInDropdownAfterThisIndex: i + 1});
            break;
          }
        }
      });
    }
  }

  togglePopupNav(evt) {
    evt.stopPropagation();
    if (this.state.isPopupNavVisible) {
      this.hidePopupNav();
    } else {
      this.showPopupNav();
    }
  }

  hidePopupNav() {
    this.setState({ isPopupNavVisible: false });
    document.removeEventListener('click', this.hidePopupNav);
  }

  showPopupNav() {
    this.setState({ isPopupNavVisible: true });
    document.addEventListener('click', this.hidePopupNav);
  }

  render() {
    const { path, showMegaDropdown, showOnMobile, isSearchVisible, breakpoint, menuLinks: { secondaryMenuLinks, mainMenuLinks } } = this.props;
    const { showLinksInDropdownAfterThisIndex, isPopupNavVisible } = this.state;
    const allMenuLinks = this.getAllLinks();

    const deviceShouldShowMegaDropdown = (
      breakpoint >= breakpoints.XL
      && touchevents()
    ) || (
      breakpoint >= breakpoints.LG
      && !touchevents()
    );

    return (
      <div className="navbar-collapse-wrapper">
        <div className={cx('collapse navbar-collapse', { 'visible-md-up': !showOnMobile })} ref="navbarCollapse">
          <div className={cx('navbar-collapse-inner')}>
            <ul className="nav navbar-nav" ref="nav">
              {
                // The actual menu
                _.map(allMenuLinks, (link, index) => {
                  if (index >= showLinksInDropdownAfterThisIndex) return false;

                  const menuItemClassName = cx('navbar-link', { 'is-active': linkIsActive(path, link.url) });

                  return (
                    <li key={link.url} className={cx({ 'secondary hidden-lg-up': link.secondary })}>

                      { this.getLinkComponent(link, menuItemClassName) }

                      { showMegaDropdown && deviceShouldShowMegaDropdown && mainMenuLinks.length && mainMenuLinks[index]
                         && <MegaDropdown sport={decodeURIComponent(_.last(link.url.split('/')))} link={mainMenuLinks[index]} /> }
                    </li>
                  );
                })
              }
            </ul>
          </div>
          {
            // Links that does not fit in the actual menu, shown in dropdown
            _.size(allMenuLinks) !== showLinksInDropdownAfterThisIndex &&
              showLinksInDropdownAfterThisIndex !== 100 &&
              <div>
                <button className="toggle-button" aria-hidden="true" onClick={evt => this.togglePopupNav(evt)}>
                  <i className="icon-ellipsis"></i>
                  <span className="structural">More</span>
                </button>
                <div className={cx({ structural: !isPopupNavVisible })}>
                  <ul className="navbar-popup" role="navigation">
                    {
                      _.map(allMenuLinks, (link, index) => {
                        if (index < showLinksInDropdownAfterThisIndex) return false;
                        const className = cx({ 'is-active': linkIsActive(path, link.url) });
                        return (
                          <li key={link.url} className={cx({ 'secondary hidden-lg-up': this.isSecondaryLink(link) })}>
                            { this.getLinkComponent(link, className) }
                          </li>
                        );
                      })
                    }
                  </ul>
                </div>
              </div>
          }
        </div>

        <div className="nav-secondary-wrapper">
          {
            // Special handling for the links that appear on the right side of a link-bar
            !_.isEmpty(secondaryMenuLinks) && !isSearchVisible &&
              <ul className="nav-secondary visible-lg-up" ref="navSecondary">
                {
                  secondaryMenuLinks.map(link =>
                    <li key={link.url}>
                      { this.getLinkComponent(link, cx('navbar-link', { 'is-active': linkIsActive(path, link.url) })) }
                    </li>
                  )
                }
              </ul>
          }
        </div>
      </div>
    );
  }
}

const menuLinks = React.PropTypes.arrayOf(React.PropTypes.shape({
  id: React.PropTypes.number,
  url: React.PropTypes.string,
  name: React.PropTypes.string,
}));

Nav.propTypes = {
  isSearchVisible: React.PropTypes.bool,
  menuLinks: React.PropTypes.shape({
    mainMenuLinks: menuLinks,
    secondaryMenuLinks: menuLinks,
  }),
  path: React.PropTypes.string,
  windowWidth: React.PropTypes.number,
  showOnMobile: React.PropTypes.bool,
  breakpoint: React.PropTypes.number,
  showMegaDropdown: React.PropTypes.bool,
};



// WEBPACK FOOTER //
// ./src/component/Navigation/Nav2_26_10_2020.jsx